import { useEffect, useState } from 'react';
import { FileClock, ArrowRight, Receipt } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { useStore } from '../store/useStore';
import { formatCurrency } from '../lib/subscriptionUtils';
import {
    fetchSubscriptionDraftInvoices,
    type SubscriptionDraftInvoice,
} from '../lib/subscriptionInvoiceUtils';

const shortDate = (iso: string | null) =>
    iso ? format(parseISO(iso), 'd MMM yyyy', { locale: it }) : '—';

/**
 * Banner in Dashboard: le fatture di abbonamento preparate dal job notturno
 * sono in bozza e aspettano solo il tuo controllo + emissione.
 */
export function SubscriptionDraftAlert() {
    const [drafts, setDrafts] = useState<SubscriptionDraftInvoice[]>([]);
    const [loading, setLoading] = useState(true);
    // Aggiornato ogni 5 minuti da useSubscriptionDraftAlerts: ricarica il dettaglio
    const subscriptionDraftCount = useStore((s) => s.subscriptionDraftCount);

    useEffect(() => {
        let cancelled = false;

        fetchSubscriptionDraftInvoices()
            .then((rows) => { if (!cancelled) setDrafts(rows); })
            .catch(() => { if (!cancelled) setDrafts([]); })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [subscriptionDraftCount]);

    if (loading || drafts.length === 0) return null;

    const total = drafts.reduce((sum, d) => sum + (d.total_amount || 0), 0);

    return (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50">
            <CardContent className="p-4 md:p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                        <FileClock className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-500" />
                        <div>
                            <h3 className="font-semibold text-sm text-amber-800">
                                {drafts.length} fattur{drafts.length === 1 ? 'a' : 'e'} di abbonamento in bozza
                                {' · '}
                                {formatCurrency(total)}
                            </h3>
                            <p className="text-xs text-amber-700/80 mt-0.5">
                                Controlla gli importi, emetti e invia allo SDI.
                            </p>
                            <div className="mt-2 space-y-1">
                                {drafts.slice(0, 3).map((draft) => (
                                    <div key={draft.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-700">
                                        <Receipt className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                        <span className="font-medium break-words">
                                            {draft.customers?.company_name ?? 'Cliente'}
                                        </span>
                                        <span className="text-gray-500">—</span>
                                        <span>{formatCurrency(draft.total_amount)}</span>
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                                            dal {shortDate(draft.subscription_period_start)} al {shortDate(draft.subscription_period_end)}
                                        </span>
                                    </div>
                                ))}
                                {drafts.length > 3 && (
                                    <p className="text-xs text-gray-500">e altre {drafts.length - 3}...</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <Link
                        to="/invoices?status=draft"
                        className="flex items-center gap-1 text-xs font-medium whitespace-nowrap text-amber-600 hover:text-amber-700"
                    >
                        Apri le bozze <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>
            </CardContent>
        </Card>
    );
}

export default SubscriptionDraftAlert;
